import { Link, useParams } from "react-router";

import { useNavigation } from "@/providers";
import { ingestionRuleIdOf } from "@/shared/domain/ingestionRules";
import { isManaged, type TriggerRecord } from "@/shared/domain/triggers";
import { useTrigger, useUpdateTrigger } from "@/shared/domain/triggers/hooks";
import { Banner, Spinner } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";

import { ManagedByIngestionRuleNote } from "../Trigger/ManagedByIngestionRuleNote";
import { systemManagedExplanation } from "../Trigger/managedTriggerExplanation";
import { isEditableInForm } from "../TriggerForm/formScope";
import { TriggerForm } from "../TriggerForm/TriggerForm";
import { TriggerFormPage } from "../TriggerForm/TriggerFormPage";

import styles from "./EditTrigger.module.css";

/**
 * Edit one stored trigger on the same page it was created on.
 *
 * Two kinds of trigger are turned away with the reason. A managed trigger's
 * definition belongs to Roboto or to an ingestion rule, and the API refuses
 * to save it. A trigger the form has no controls for would be opened
 * half-represented, and saving it would quietly rewrite the parts that were
 * never shown.
 */
export function EditTrigger() {
  const { triggerName } = useParams<{ triggerName: string }>();
  const triggerQuery = useTrigger(triggerName);

  return (
    <TriggerFormPage
      title="Edit trigger"
      subtitle={
        "Change what this trigger watches, how often it fires, and what it runs."
      }
      crumbs={
        triggerName === undefined ? null : (
          <Link to={Goto.createTriggerRoute(triggerName)}>{triggerName}</Link>
        )
      }
    >
      {triggerQuery.isLoading ? <Spinner /> : null}
      {triggerQuery.error === null ? null : (
        <Banner
          variant="error"
          className={styles.notice}
          data-testid="edit-trigger-error"
        >
          {triggerQuery.error.message}
        </Banner>
      )}
      {triggerQuery.data === undefined ? null : (
        <Editor record={triggerQuery.data} />
      )}
    </TriggerFormPage>
  );
}

function Editor({ record }: { record: TriggerRecord }) {
  const { mutateAsync: updateTrigger } = useUpdateTrigger();
  const { goto } = useNavigation();
  const backToTrigger = () => goto.trigger({ triggerName: record.name });

  if (isManaged(record) || !isEditableInForm(record)) {
    return (
      <Banner
        variant="warning"
        className={styles.notice}
        data-testid="edit-trigger-unavailable"
        description={<WhyNotEditable record={record} />}
        action={
          <Link to={Goto.createTriggerRoute(record.name)}>
            Back to {record.name}
          </Link>
        }
      />
    );
  }

  return (
    <TriggerForm
      mode="edit"
      initial={record}
      onSubmit={async (request) => {
        // The name is the trigger's identifier and the update endpoint has no
        // field for it; everything else the builder produces is an update.
        const { name: _name, ...updates } = request;
        await updateTrigger({ name: record.name, updates });
        backToTrigger();
      }}
      onCancel={backToTrigger}
    />
  );
}

function WhyNotEditable({ record }: { record: TriggerRecord }) {
  const ruleId = ingestionRuleIdOf(record);
  if (ruleId !== null) {
    return <ManagedByIngestionRuleNote ruleId={ruleId} />;
  }
  if (isManaged(record)) {
    return <>{systemManagedExplanation(record.managed_by)}</>;
  }
  return (
    <>
      This trigger watches something the form cannot show, so editing it here
      would drop what is not on the page. It can still be enabled, disabled, and
      deleted from its own page.
    </>
  );
}
